import React from 'react';
import ContentViewComponent from '../../../../Component/ContentViewComponent/ContentViewComponent';
import ApiHandler from '../../../../Handler/ApiHandler';
import { getRoutePathname } from '../../../../Handler/RouteHandler';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Chip, IconButton, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import { Add, ArrowBack, ArrowForward, CalendarMonth, Delete, LockClock, LockOpen } from '@mui/icons-material';
import { dispatch } from '../../../../App';
import { SnackbarOpen } from '../../../../Reducer/SnackBarReducer/SnackBarSlice';
import moment from 'moment';
import Swal from 'sweetalert2';

const WEEKDAY_LABELS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
const HOUR_HEIGHT = 42;
const LABEL_WIDTH = 46;
const latestPublicAppointmentsRequestLimit = 6;
const latestPublicAppointmentsPollIntervalMs = 30000;

function formatPrice(value) {
    return `${Number(value || 0).toFixed(2)} €`;
}

function toIsoDate(date) {
    return moment(date).format('YYYY-MM-DD');
}

function startOfWeek(date) {
    const day = date.getDay();
    const mondayOffset = day === 0 ? -6 : 1 - day;
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + mondayOffset);
    return d;
}

function getWeekDays(weekStart) {
    const days = [];
    for (let i = 0; i < 7; i++) {
        const date = new Date(weekStart);
        date.setDate(weekStart.getDate() + i);
        days.push({
            date,
            isoDate: toIsoDate(date),
            dayLabel: WEEKDAY_LABELS[i],
            dayOfWeek: i + 1,
        });
    }
    return days;
}

function formatClosure(closure) {
    if (closure.isClosed) {
        return 'Fermeture journée';
    }
    return `Fermé ${closure.startTime} - ${closure.endTime}`;
}

function sortAppointments(rows) {
    return [...rows].sort((a, b) => moment(a.startAt).valueOf() - moment(b.startAt).valueOf());
}

function sortClosures(rows) {
    return [...rows].sort((a, b) => {
        if (a.isClosed && !b.isClosed) return -1;
        if (!a.isClosed && b.isClosed) return 1;
        return (a.startTime || '').localeCompare(b.startTime || '');
    });
}

function sortOpenings(rows) {
    return [...rows].sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''));
}

function mergeTimeIntervals(intervals) {
    const normalized = [];
    (intervals || []).forEach((interval) => {
        const start = toMinutes(interval.startTime);
        const end = toMinutes(interval.endTime);
        if (start === null || end === null || start >= end) {
            return;
        }
        normalized.push({ start, end });
    });

    if (normalized.length < 1) {
        return [];
    }

    normalized.sort((a, b) => a.start - b.start);
    const merged = [normalized[0]];
    for (let i = 1; i < normalized.length; i += 1) {
        const current = normalized[i];
        const last = merged[merged.length - 1];
        if (current.start <= last.end) {
            last.end = Math.max(last.end, current.end);
        } else {
            merged.push(current);
        }
    }

    return merged.map((interval) => ({
        startTime: `${String(Math.floor(interval.start / 60)).padStart(2, '0')}:${String(interval.start % 60).padStart(2, '0')}`,
        endTime: `${String(Math.floor(interval.end / 60)).padStart(2, '0')}:${String(interval.end % 60).padStart(2, '0')}`,
    }));
}

function toMinutes(time) {
    if (!time || typeof time !== 'string' || !/^\d{2}:\d{2}$/.test(time)) {
        return null;
    }
    const [h, m] = time.split(':').map((x) => parseInt(x, 10));
    if (Number.isNaN(h) || Number.isNaN(m)) {
        return null;
    }
    return (h * 60) + m;
}

function getTimeFromDate(value) {
    return moment(value).format('HH:mm');
}

function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
}

function buildTimeline(openIntervals) {
    if (!Array.isArray(openIntervals) || openIntervals.length < 1) {
        return null;
    }

    let minStart = null;
    let maxEnd = null;

    openIntervals.forEach((interval) => {
        const start = toMinutes(interval.startTime);
        const end = toMinutes(interval.endTime);
        if (start === null || end === null || start >= end) {
            return;
        }
        if (minStart === null || start < minStart) minStart = start;
        if (maxEnd === null || end > maxEnd) maxEnd = end;
    });

    if (minStart === null || maxEnd === null || minStart >= maxEnd) {
        return null;
    }

    const start = Math.max(0, (Math.floor(minStart / 60) * 60) - 120);
    const end = Math.min((24 * 60), (Math.ceil(maxEnd / 60) * 60) + 120);
    const totalMinutes = Math.max(60, end - start);
    const totalHeight = (totalMinutes / 60) * HOUR_HEIGHT;

    const timeMarks = [];
    const firstHalfHour = Math.ceil(start / 30) * 30;
    for (let minute = firstHalfHour; minute <= end; minute += 30) {
        timeMarks.push(minute);
    }

    return {
        start,
        end,
        totalMinutes,
        totalHeight,
        timeMarks,
    };
}

function minuteToY(minute, timeline) {
    return ((minute - timeline.start) / timeline.totalMinutes) * timeline.totalHeight;
}

function CrmAppointmentList() {
    const [loading, setLoading] = React.useState(true);
    const [weekLoading, setWeekLoading] = React.useState(true);
    const [latestPublicAppointmentsLoading, setLatestPublicAppointmentsLoading] = React.useState(true);
    const [latestPublicAppointments, setLatestPublicAppointments] = React.useState([]);
    const [latestPublicAppointmentsCount, setLatestPublicAppointmentsCount] = React.useState(0);
    const [latestPublicAppointmentsMarkingRead, setLatestPublicAppointmentsMarkingRead] = React.useState(false);
    const [breadcrumbs, setBreadcrumbs] = React.useState({});
    const [weekStartDate, setWeekStartDate] = React.useState(startOfWeek(new Date()));
    const [appointmentsByDate, setAppointmentsByDate] = React.useState({});
    const [closuresByDate, setClosuresByDate] = React.useState({});
    const [openingsByDate, setOpeningsByDate] = React.useState({});
    const [availabilityByDay, setAvailabilityByDay] = React.useState({});
    const [nowTick, setNowTick] = React.useState(moment());
    const navigate = useNavigate();

    const weekDays = React.useMemo(() => getWeekDays(weekStartDate), [weekStartDate]);
    const periodLabel = `${moment(weekDays[0].date).format('DD/MM')} - ${moment(weekDays[6].date).format('DD/MM/YYYY')}`;
    const weeklyMetrics = React.useMemo(() => {
        let amount = 0;
        let billableCount = 0;

        Object.values(appointmentsByDate).forEach((rows) => {
            (rows || []).forEach((item) => {
                const status = String(item?.status || '').trim().toLowerCase();
                if (status === 'annulé' || status === 'annule' || status === 'cancelled') {
                    return;
                }

                amount += Number(item?.effectivePrice ?? item?.prestationPrice ?? item?.price ?? 0);
                billableCount += 1;
            });
        });

        return { amount, billableCount };
    }, [appointmentsByDate]);

    const loadWeek = React.useCallback(() => {
        const dateFrom = weekDays[0].isoDate;
        const dateTo = weekDays[6].isoDate;
        setWeekLoading(true);

        ApiHandler.get(
            {
                route: 'crm_appointment_list',
                data: {
                    limit: 5000,
                    offset: 0,
                    sortName: 'startAt',
                    sortDirection: 'asc',
                    dateFrom,
                    dateTo,
                },
            },
            (response) => {
                if (response.status !== 200) {
                    dispatch(
                        SnackbarOpen({
                            text:
                                response.error && response.error.message
                                    ? response.error.message
                                    : "Une erreur inattendu s'est produite.",
                            variant: 'error',
                        }),
                    );
                    setWeekLoading(false);
                    return;
                }

                const appointmentMap = {};
                (response.data.data || []).forEach((item) => {
                    const key = moment(item.startAt).format('YYYY-MM-DD');
                    if (!appointmentMap[key]) appointmentMap[key] = [];
                    appointmentMap[key].push(item);
                });
                Object.keys(appointmentMap).forEach((key) => {
                    appointmentMap[key] = sortAppointments(appointmentMap[key]);
                });
                setAppointmentsByDate(appointmentMap);

                const closureMap = {};
                (response.data.closures || []).forEach((item) => {
                    const key = item.date;
                    if (!closureMap[key]) closureMap[key] = [];
                    closureMap[key].push(item);
                });
                Object.keys(closureMap).forEach((key) => {
                    closureMap[key] = sortClosures(closureMap[key]);
                });
                setClosuresByDate(closureMap);

                const openingMap = {};
                (response.data.openings || []).forEach((item) => {
                    const key = item.date;
                    if (!openingMap[key]) openingMap[key] = [];
                    openingMap[key].push(item);
                });
                Object.keys(openingMap).forEach((key) => {
                    openingMap[key] = sortOpenings(openingMap[key]);
                });
                setOpeningsByDate(openingMap);

                const availabilityMap = {};
                (response.data.availabilities || []).forEach((item) => {
                    if (!item || !item.isActive || !item.startTime || !item.endTime) {
                        return;
                    }
                    const day = Number(item.dayOfWeek);
                    if (!availabilityMap[day]) availabilityMap[day] = [];
                    availabilityMap[day].push({
                        startTime: item.startTime,
                        endTime: item.endTime,
                    });
                });
                Object.keys(availabilityMap).forEach((day) => {
                    availabilityMap[day].sort((a, b) => a.startTime.localeCompare(b.startTime));
                });
                setAvailabilityByDay(availabilityMap);
                setWeekLoading(false);
            },
        );
    }, [weekDays]);

    React.useEffect(() => {
        setBreadcrumbs({
            title: 'CRM',
            context: 'Planning de la semaine',
            description: '',
            links: [],
        });
        setTimeout(() => setLoading(false), 450);
    }, []);

    React.useEffect(() => {
        loadWeek();
    }, [loadWeek]);

    const loadLatestPublicAppointments = React.useCallback((withLoader = false) => {
        if (withLoader) {
            setLatestPublicAppointmentsLoading(true);
        }

        ApiHandler.get(
            {
                route: 'crm_appointment_notification_latest',
                data: {
                    limit: latestPublicAppointmentsRequestLimit,
                },
            },
            (response) => {
                if (response.status !== 200) {
                    if (withLoader) {
                        setLatestPublicAppointmentsLoading(false);
                    }
                    return;
                }

                const count = Number(response.data?.count ?? 0);
                setLatestPublicAppointmentsCount(Number.isNaN(count) ? 0 : Math.max(0, count));
                setLatestPublicAppointments(Array.isArray(response.data?.appointments) ? response.data.appointments : []);
                setLatestPublicAppointmentsLoading(false);
            },
        );
    }, []);

    React.useEffect(() => {
        loadLatestPublicAppointments(true);
    }, [loadLatestPublicAppointments]);

    React.useEffect(() => {
        const intervalId = window.setInterval(() => {
            loadLatestPublicAppointments(false);
        }, latestPublicAppointmentsPollIntervalMs);

        const onVisibilityChange = () => {
            if (!document.hidden) {
                loadLatestPublicAppointments(false);
            }
        };

        document.addEventListener('visibilitychange', onVisibilityChange);

        return () => {
            window.clearInterval(intervalId);
            document.removeEventListener('visibilitychange', onVisibilityChange);
        };
    }, [loadLatestPublicAppointments]);

    const markLatestPublicAppointmentsAsRead = React.useCallback(() => {
        if (latestPublicAppointmentsMarkingRead || latestPublicAppointmentsCount < 1) {
            return;
        }

        setLatestPublicAppointmentsMarkingRead(true);
        ApiHandler.post({ route: 'crm_appointment_notification_read', data: {} }, (response) => {
            setLatestPublicAppointmentsMarkingRead(false);

            if (response.status !== 200) {
                return;
            }

            setLatestPublicAppointmentsCount(0);
            setLatestPublicAppointments([]);
            loadLatestPublicAppointments(false);
            window.dispatchEvent(new Event('crm-appointment-notifications-refresh'));
        });
    }, [latestPublicAppointmentsCount, latestPublicAppointmentsMarkingRead, loadLatestPublicAppointments]);

    React.useEffect(() => {
        const id = setInterval(() => {
            setNowTick(moment());
        }, 60000);

        return () => clearInterval(id);
    }, []);

    const showError = (response) => {
        dispatch(
            SnackbarOpen({
                text:
                    response && response.error && response.error.message
                        ? response.error.message
                        : "Une erreur inattendu s'est produite.",
                variant: 'error',
            }),
        );
    };

    const addClosure = async (isoDate) => {
        const result = await Swal.fire({
            title: `Ajouter une fermeture (${moment(isoDate).format('DD/MM/YYYY')})`,
            html:
                '<div style="display:flex;flex-direction:column;gap:10px;text-align:left">' +
                '<label><input id="swal-is-closed" type="checkbox" /> Fermeture journée entière</label>' +
                '<input id="swal-start" class="swal2-input" type="time" placeholder="Début" />' +
                '<input id="swal-end" class="swal2-input" type="time" placeholder="Fin" />' +
                '<input id="swal-note" class="swal2-input" type="text" placeholder="Note (optionnel)" />' +
                '</div>',
            focusConfirm: false,
            showCancelButton: true,
            confirmButtonText: 'Enregistrer',
            cancelButtonText: 'Annuler',
            preConfirm: () => {
                const isClosed = !!document.getElementById('swal-is-closed')?.checked;
                const startTime = document.getElementById('swal-start')?.value || null;
                const endTime = document.getElementById('swal-end')?.value || null;
                const note = document.getElementById('swal-note')?.value || null;

                if (!isClosed) {
                    if (!startTime || !endTime) {
                        Swal.showValidationMessage('Heure de début et heure de fin requises.');
                        return false;
                    }
                    if (startTime >= endTime) {
                        Swal.showValidationMessage('L\'heure de fin doit être supérieure à l\'heure de début.');
                        return false;
                    }
                }

                return { isClosed, startTime, endTime, note };
            },
        });

        if (!result.isConfirmed || !result.value) {
            return;
        }

        const payload = {
            date: isoDate,
            isClosed: result.value.isClosed,
            startTime: result.value.isClosed ? null : result.value.startTime,
            endTime: result.value.isClosed ? null : result.value.endTime,
            note: result.value.note,
        };

        ApiHandler.post(
            { route: 'crm_appointment_closure_add', data: payload },
            (response) => {
                if (response.status === 200 || response.status === 201) {
                    dispatch(SnackbarOpen({ text: 'Fermeture ajoutée avec succès', variant: 'success' }));
                    loadWeek();
                    return;
                }

                if (response.status === 400 && response.error && response.error.fields && response.error.fields.length > 0) {
                    dispatch(SnackbarOpen({ text: response.error.fields[0].message || 'Erreur de validation', variant: 'error' }));
                    return;
                }

                showError(response);
            },
        );
    };

    const addOpening = async (isoDate) => {
        const result = await Swal.fire({
            title: `Ajouter une ouverture (${moment(isoDate).format('DD/MM/YYYY')})`,
            html:
                '<div style="display:flex;flex-direction:column;gap:10px;text-align:left">' +
                '<input id="swal-start" class="swal2-input" type="time" placeholder="Début" />' +
                '<input id="swal-end" class="swal2-input" type="time" placeholder="Fin" />' +
                '<input id="swal-note" class="swal2-input" type="text" placeholder="Note (optionnel)" />' +
                '</div>',
            focusConfirm: false,
            showCancelButton: true,
            confirmButtonText: 'Enregistrer',
            cancelButtonText: 'Annuler',
            preConfirm: () => {
                const startTime = document.getElementById('swal-start')?.value || null;
                const endTime = document.getElementById('swal-end')?.value || null;
                const note = document.getElementById('swal-note')?.value || null;

                if (!startTime || !endTime) {
                    Swal.showValidationMessage('Heure de début et heure de fin requises.');
                    return false;
                }
                if (startTime >= endTime) {
                    Swal.showValidationMessage('L\'heure de fin doit être supérieure à l\'heure de début.');
                    return false;
                }

                return { startTime, endTime, note };
            },
        });

        if (!result.isConfirmed || !result.value) {
            return;
        }

        const payload = {
            date: isoDate,
            startTime: result.value.startTime,
            endTime: result.value.endTime,
            note: result.value.note,
        };

        ApiHandler.post(
            { route: 'crm_appointment_opening_add', data: payload },
            (response) => {
                if (response.status === 200 || response.status === 201) {
                    dispatch(SnackbarOpen({ text: 'Ouverture ajoutée avec succès', variant: 'success' }));
                    loadWeek();
                    return;
                }

                if (response.status === 400 && response.error && response.error.fields && response.error.fields.length > 0) {
                    dispatch(SnackbarOpen({ text: response.error.fields[0].message || 'Erreur de validation', variant: 'error' }));
                    return;
                }

                showError(response);
            },
        );
    };

    const removeClosure = (closureId) => {
        Swal.fire({
            title: 'Supprimer cette fermeture ?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Confirmer',
            cancelButtonText: 'Annuler',
            reverseButtons: true,
        }).then((result) => {
            if (!result.isConfirmed) {
                return;
            }

            ApiHandler.delete(
                { route: 'crm_appointment_closure_delete', params: { id: closureId } },
                (response) => {
                    if (response.status === 200) {
                        dispatch(SnackbarOpen({ text: 'Fermeture supprimée', variant: 'success' }));
                        loadWeek();
                        return;
                    }
                    showError(response);
                },
            );
        });
    };

    const removeOpening = (openingId) => {
        Swal.fire({
            title: 'Supprimer cette ouverture ?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Confirmer',
            cancelButtonText: 'Annuler',
            reverseButtons: true,
        }).then((result) => {
            if (!result.isConfirmed) {
                return;
            }

            ApiHandler.delete(
                { route: 'crm_appointment_opening_delete', params: { id: openingId } },
                (response) => {
                    if (response.status === 200) {
                        dispatch(SnackbarOpen({ text: 'Ouverture supprimée', variant: 'success' }));
                        loadWeek();
                        return;
                    }
                    showError(response);
                },
            );
        });
    };

    const prevWeek = () => {
        setWeekStartDate((value) => {
            const d = new Date(value);
            d.setDate(d.getDate() - 7);
            return d;
        });
    };

    const nextWeek = () => {
        setWeekStartDate((value) => {
            const d = new Date(value);
            d.setDate(d.getDate() + 7);
            return d;
        });
    };

    const openDateSearch = async () => {
        const result = await Swal.fire({
            title: 'Rechercher une date',
            input: 'text',
            inputValue: moment(weekStartDate).format('DD/MM/YYYY'),
            inputPlaceholder: 'DD/MM/YYYY',
            showCancelButton: true,
            confirmButtonText: 'Afficher la semaine',
            cancelButtonText: 'Annuler',
            inputValidator: (value) => {
                if (!value) {
                    return 'Veuillez saisir une date.';
                }
                if (!moment(value, 'DD/MM/YYYY', true).isValid()) {
                    return 'Format attendu: DD/MM/YYYY';
                }
                return null;
            },
        });

        if (!result.isConfirmed || !result.value) {
            return;
        }

        const parsed = moment(result.value, 'DD/MM/YYYY', true);
        if (!parsed.isValid()) {
            dispatch(SnackbarOpen({ text: 'Veuillez saisir une date valide (DD/MM/YYYY).', variant: 'error' }));
            return;
        }

        setWeekStartDate(startOfWeek(parsed.toDate()));
    };

    return (
        <ContentViewComponent loading={loading} breadcrumbs={breadcrumbs}>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: { xs: 'stretch', md: 'center' },
                    flexDirection: { xs: 'column', md: 'row' },
                    mb: 2,
                    gap: 1.2,
                }}
            >
                <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{
                        border: '1px solid #e4e7ec',
                        backgroundColor: '#f8fafc',
                        borderRadius: '12px',
                        px: 0.6,
                        py: 0.4,
                        width: 'fit-content',
                    }}
                >
                    <IconButton onClick={prevWeek} size="small" sx={{ backgroundColor: '#fff' }}><ArrowBack /></IconButton>
                    <Typography sx={{ fontWeight: 700, fontSize: { xs: 15, md: 18 }, color: '#1f2937', px: 0.4 }}>
                        {periodLabel}
                    </Typography>
                    <IconButton onClick={nextWeek} size="small" sx={{ backgroundColor: '#fff' }}><ArrowForward /></IconButton>
                </Stack>
                <Box
                    sx={{
                        border: '1px solid #dbe3ef',
                        borderRadius: '12px',
                        px: 1,
                        py: 0.7,
                        backgroundColor: '#f8fbff',
                        width: { xs: '100%', md: 'auto' },
                        minWidth: { md: 220 },
                    }}
                >
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#516175', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                        CA estimé semaine
                    </Typography>
                    <Typography sx={{ fontSize: 20, fontWeight: 800, color: '#0f5132', lineHeight: 1.2 }}>
                        {formatPrice(weeklyMetrics.amount)}
                    </Typography>
                    <Typography sx={{ fontSize: 11.5, color: '#516175' }}>
                        {weeklyMetrics.billableCount} RDV pris en compte
                    </Typography>
                </Box>
                <Stack
                    direction="row"
                    spacing={0.8}
                    alignItems="center"
                    sx={{
                        border: '1px solid #e4e7ec',
                        borderRadius: '12px',
                        px: 0.6,
                        py: 0.4,
                        backgroundColor: '#ffffff',
                        width: 'fit-content',
                    }}
                >
                    <Tooltip title="Aller à une semaine" placement="left" arrow>
                        <IconButton onClick={openDateSearch} size="small" sx={{ backgroundColor: '#f3f4f6' }}>
                            <CalendarMonth sx={{ fontSize: 24 }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Ajouter un rendez-vous" placement="left" arrow>
                        <IconButton onClick={() => navigate(getRoutePathname('crm_appointment', { id: 'add' }))} sx={{ backgroundColor: '#e8f0ff' }}>
                            <Add sx={{ fontSize: 30 }} />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Box>

            <Box
                sx={{
                    border: '1px solid #d8e8ff',
                    borderRadius: '12px',
                    p: 1.2,
                    mb: 1.4,
                    background: 'linear-gradient(120deg, #f0f7ff 0%, #ffffff 100%)',
                }}
            >
                <Typography sx={{ fontSize: 12, color: '#1d4ed8', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.45 }}>
                    Nouveaux rendez-vous clients
                </Typography>
                <Typography sx={{ fontSize: 12, color: '#334155', mb: 0.8 }}>
                    {latestPublicAppointmentsCount > 0
                        ? `${latestPublicAppointmentsCount} nouveau(x) depuis votre dernière visite`
                        : 'Aucun nouveau rendez-vous depuis votre dernière visite'}
                </Typography>
                {latestPublicAppointmentsCount > 0 && (
                    <Box sx={{ mb: 0.8 }}>
                        <Button
                            size="small"
                            variant="outlined"
                            disabled={latestPublicAppointmentsMarkingRead}
                            onClick={markLatestPublicAppointmentsAsRead}
                            sx={{ textTransform: 'none', fontWeight: 700 }}
                        >
                            {latestPublicAppointmentsMarkingRead ? 'Mise à jour...' : 'Marquer comme vus'}
                        </Button>
                    </Box>
                )}

                {latestPublicAppointmentsLoading ? (
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={0.8}>
                        <Skeleton variant="rounded" height={66} sx={{ width: '100%', borderRadius: '10px' }} />
                        <Skeleton variant="rounded" height={66} sx={{ width: '100%', borderRadius: '10px' }} />
                    </Stack>
                ) : latestPublicAppointments.length < 1 ? (
                    <Typography sx={{ fontSize: 11.5, color: '#64748b' }}>
                        Aucun nouvel élément à afficher.
                    </Typography>
                ) : (
                    <Stack direction="column" spacing={0.8} sx={{ maxHeight: { xs: 240, md: 280 }, overflowY: 'auto', pr: 0.4 }}>
                        {latestPublicAppointments.map((item) => (
                            <Box
                                key={`latest-public-appointment-${item.id}`}
                                onClick={() => navigate(getRoutePathname('crm_appointment_day', { date: moment(item.startAt).format('YYYY-MM-DD') }))}
                                sx={{
                                    border: '1px solid #dbe7fb',
                                    borderRadius: '10px',
                                    px: 0.9,
                                    py: 0.7,
                                    backgroundColor: '#ffffff',
                                    cursor: 'pointer',
                                    '&:hover': {
                                        backgroundColor: '#f8fbff',
                                        borderColor: '#bfd3f4',
                                    },
                                }}
                            >
                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={0.8} justifyContent="space-between">
                                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#0f172a' }}>
                                        {item.customerLastname || ''} {item.customerFirstname || ''} · {item.prestationLabel || 'Prestation'}
                                    </Typography>
                                    <Typography sx={{ fontSize: 11.5, color: '#475569', fontWeight: 600 }}>
                                        Créé le {item.createdAt ? moment(item.createdAt).format('DD/MM/YYYY HH:mm') : '--/--/---- --:--'}
                                    </Typography>
                                </Stack>
                                <Typography sx={{ fontSize: 11.5, color: '#334155', mt: 0.2 }}>
                                    RDV: {moment(item.startAt).format('DD/MM/YYYY HH:mm')} - {moment(item.endAt).format('HH:mm')}
                                </Typography>
                            </Box>
                        ))}
                        {latestPublicAppointmentsCount > latestPublicAppointments.length && (
                            <Typography sx={{ fontSize: 11.5, color: '#475569' }}>
                                +{latestPublicAppointmentsCount - latestPublicAppointments.length} autre(s) rendez-vous non affiché(s)
                            </Typography>
                        )}
                    </Stack>
                )}
            </Box>

            <Box sx={{ display: 'grid', gap: 1.3, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0,1fr))', lg: 'repeat(4, minmax(0,1fr))' } }}>
                {weekDays.map((day) => {
                    if (weekLoading) {
                        return (
                            <Box
                                key={day.isoDate}
                                sx={{
                                    border: '1px solid #e8ebf0',
                                    borderRadius: '14px',
                                    p: 1.1,
                                    backgroundColor: '#fff',
                                    boxShadow: '0 2px 8px rgba(17, 24, 39, 0.04)',
                                }}
                            >
                                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                                    <Box>
                                        <Skeleton variant="text" width={90} height={18} />
                                        <Skeleton variant="text" width={54} height={24} />
                                    </Box>
                                    <Stack direction="row" spacing={0.6}>
                                        <Skeleton variant="circular" width={28} height={28} />
                                        <Skeleton variant="circular" width={28} height={28} />
                                        <Skeleton variant="circular" width={28} height={28} />
                                    </Stack>
                                </Stack>
                                <Stack direction="row" spacing={0.6} sx={{ mb: 1 }}>
                                    <Skeleton variant="rounded" width={82} height={24} />
                                    <Skeleton variant="rounded" width={96} height={24} />
                                    <Skeleton variant="rounded" width={96} height={24} />
                                </Stack>
                                <Skeleton variant="rounded" width="100%" height={230} />
                            </Box>
                        );
                    }

                    const appointments = appointmentsByDate[day.isoDate] || [];
                    const closures = closuresByDate[day.isoDate] || [];
                    const openings = openingsByDate[day.isoDate] || [];
                    const openIntervalsRaw = [
                        ...(availabilityByDay[day.dayOfWeek] || []),
                        ...openings.map((item) => ({ startTime: item.startTime, endTime: item.endTime })),
                    ];
                    const openIntervals = mergeTimeIntervals(openIntervalsRaw);
                    const timeline = buildTimeline(openIntervals);
                    const isToday = nowTick.format('YYYY-MM-DD') === day.isoDate;

                    const closureBlocks = [];
                    const openingBlocks = [];
                    const closedBlocks = [];
                    if (timeline) {
                        let cursor = timeline.start;
                        openIntervals.forEach((interval, index) => {
                            const start = toMinutes(interval.startTime);
                            const end = toMinutes(interval.endTime);
                            if (start === null || end === null || start >= end) return;

                            if (start > cursor) {
                                closedBlocks.push({
                                    key: `closed-${day.isoDate}-${index}`,
                                    start: cursor,
                                    end: start,
                                });
                            }
                            cursor = Math.max(cursor, end);
                        });
                        if (cursor < timeline.end) {
                            closedBlocks.push({
                                key: `closed-${day.isoDate}-tail`,
                                start: cursor,
                                end: timeline.end,
                            });
                        }

                        openings.forEach((opening) => {
                            const start = toMinutes(opening.startTime);
                            const end = toMinutes(opening.endTime);
                            if (start === null || end === null || start >= end) return;
                            openingBlocks.push({
                                key: `opening-${opening.id}`,
                                start,
                                end,
                                label: `Ouvert ${opening.startTime} - ${opening.endTime}`,
                                note: opening.note,
                                id: opening.id,
                            });
                        });

                        closures.forEach((closure, index) => {
                            if (closure.isClosed) {
                                openIntervals.forEach((interval, intervalIndex) => {
                                    const start = toMinutes(interval.startTime);
                                    const end = toMinutes(interval.endTime);
                                    if (start === null || end === null || start >= end) return;
                                    closureBlocks.push({
                                        key: `day-closure-${index}-${intervalIndex}`,
                                        start,
                                        end,
                                        label: 'Fermeture journée',
                                        note: closure.note,
                                        id: closure.id,
                                        singleDelete: intervalIndex === 0,
                                    });
                                });
                            } else {
                                const start = toMinutes(closure.startTime);
                                const end = toMinutes(closure.endTime);
                                if (start === null || end === null || start >= end) return;
                                closureBlocks.push({
                                    key: `closure-${closure.id}`,
                                    start,
                                    end,
                                    label: formatClosure(closure),
                                    note: closure.note,
                                    id: closure.id,
                                    singleDelete: true,
                                });
                            }
                        });
                    }

                    return (
                        <Box
                            key={day.isoDate}
                            sx={{
                                border: isToday ? '1px solid #cfe1ff' : '1px solid #e8ebf0',
                                borderRadius: '14px',
                                p: 1.1,
                                backgroundColor: isToday ? '#f7fbff' : '#fff',
                                boxShadow: isToday ? '0 4px 16px rgba(30, 64, 175, 0.08)' : '0 2px 8px rgba(17, 24, 39, 0.04)',
                                transition: 'box-shadow 0.2s ease, transform 0.2s ease',
                                '&:hover': {
                                    boxShadow: '0 8px 20px rgba(17, 24, 39, 0.10)',
                                    transform: 'translateY(-1px)',
                                },
                            }}
                        >
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.8 }}>
                                <Tooltip title="Voir le detail de la journée" arrow>
                                    <Box
                                        sx={{ cursor: 'pointer', px: 0.4, py: 0.2, borderRadius: '8px', '&:hover': { backgroundColor: '#f5f7fb' } }}
                                        onClick={() => navigate(getRoutePathname('crm_appointment_day', { date: day.isoDate }))}
                                    >
                                        <Typography sx={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>{day.dayLabel}</Typography>
                                        <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#111827', letterSpacing: 0.2 }}>
                                            {moment(day.date).format('DD/MM')}
                                        </Typography>
                                    </Box>
                                </Tooltip>
                                <Stack direction="row" spacing={0.5}>
                                    <Tooltip title="Ajouter une ouverture" arrow>
                                        <IconButton size="small" onClick={() => addOpening(day.isoDate)} sx={{ backgroundColor: '#e8f5eb', color: '#14532d' }}>
                                            <LockOpen sx={{ fontSize: 18, color: '#14532d' }} />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Ajouter une fermeture" arrow>
                                        <IconButton size="small" onClick={() => addClosure(day.isoDate)} sx={{ backgroundColor: '#fdecec', color: '#7f1d1d' }}>
                                            <LockClock sx={{ fontSize: 18, color: '#7f1d1d' }} />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Ajouter un rendez-vous" arrow>
                                        <IconButton size="small" onClick={() => navigate(`${getRoutePathname('crm_appointment', { id: 'add' })}?date=${day.isoDate}`)} sx={{ backgroundColor: '#e8f0ff', color: '#1e3a8a' }}>
                                            <Add sx={{ fontSize: 18, color: '#1e3a8a' }} />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            </Stack>

                            <Stack direction="row" spacing={0.6} sx={{ mb: 1, flexWrap: 'wrap', rowGap: 0.6 }}>
                                <Chip size="small" label={`${appointments.length} RDV`} sx={{ fontSize: 11, fontWeight: 700, backgroundColor: '#e8f0ff', color: '#1e3a8a' }} />
                                <Chip size="small" label={`${closures.length} fermetures`} sx={{ fontSize: 11, fontWeight: 700, backgroundColor: '#fdecea', color: '#7f1d1d' }} />
                                <Chip size="small" label={`${openings.length} ouvertures`} sx={{ fontSize: 11, fontWeight: 700, backgroundColor: '#e4f7e8', color: '#14532d' }} />
                            </Stack>

                            {!timeline ? (
                                <Typography sx={{ fontSize: 12, color: '#808080' }}>Aucune ouverture globale ce jour.</Typography>
                            ) : (
                                <Box
                                    sx={{
                                        position: 'relative',
                                        border: '1px solid #e7ebf2',
                                        borderRadius: '10px',
                                        overflow: 'hidden',
                                        background: 'linear-gradient(90deg, #f8fafc 0px, #f8fafc 48px, #ffffff 48px, #ffffff 100%)',
                                    }}
                                >
                                    <Box sx={{ height: `${timeline.totalHeight}px` }} />

                                    {closedBlocks.map((item) => {
                                        const start = clamp(item.start, timeline.start, timeline.end);
                                        const end = clamp(item.end, timeline.start, timeline.end);
                                        if (end <= start) return null;
                                        const top = minuteToY(start, timeline);
                                        const height = Math.max(2, minuteToY(end, timeline) - top);

                                        return (
                                            <Box
                                                key={item.key}
                                                sx={{
                                                    position: 'absolute',
                                                    top: `${top}px`,
                                                    left: `${LABEL_WIDTH + 4}px`,
                                                    right: '4px',
                                                    minHeight: '2px',
                                                    height: `${height}px`,
                                                    borderRadius: '7px',
                                                    backgroundColor: '#fdeaea',
                                                    zIndex: 2,
                                                }}
                                            />
                                        );
                                    })}

                                    {openingBlocks.map((item) => {
                                        const start = clamp(item.start, timeline.start, timeline.end);
                                        const end = clamp(item.end, timeline.start, timeline.end);
                                        if (end <= start) return null;
                                        const top = minuteToY(start, timeline);
                                        const height = Math.max(18, minuteToY(end, timeline) - top);

                                        return (
                                            <Box
                                                key={item.key}
                                                sx={{
                                                    position: 'absolute',
                                                    top: `${top}px`,
                                                    left: `${LABEL_WIDTH + 4}px`,
                                                    right: '4px',
                                                    minHeight: '18px',
                                                    height: `${height}px`,
                                                    borderRadius: '7px',
                                                    overflow: 'hidden',
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        inset: 0,
                                                        borderRadius: '7px',
                                                        backgroundColor: '#d7f0de',
                                                        zIndex: 2,
                                                    }}
                                                />
                                                <Box
                                                    sx={{
                                                        position: 'relative',
                                                        zIndex: 7,
                                                        px: 0.6,
                                                        py: 0.2,
                                                        fontSize: 10.5,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        height: '100%',
                                                        color: '#0f172a',
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    <Box sx={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', pr: 0.5 }}>
                                                        {item.label}{item.note ? ` - ${item.note}` : ''}
                                                    </Box>
                                                    <IconButton size="small" onClick={() => removeOpening(item.id)} sx={{ p: 0.2 }}>
                                                        <Delete sx={{ fontSize: 12 }} />
                                                    </IconButton>
                                                </Box>
                                            </Box>
                                        );
                                    })}

                                    {closureBlocks.map((item) => {
                                        const start = clamp(item.start, timeline.start, timeline.end);
                                        const end = clamp(item.end, timeline.start, timeline.end);
                                        if (end <= start) return null;
                                        const top = minuteToY(start, timeline);
                                        const height = Math.max(18, minuteToY(end, timeline) - top);

                                        return (
                                            <Box
                                                key={item.key}
                                                sx={{
                                                    position: 'absolute',
                                                    top: `${top}px`,
                                                    left: `${LABEL_WIDTH + 4}px`,
                                                    right: '4px',
                                                    minHeight: '18px',
                                                    height: `${height}px`,
                                                    borderRadius: '7px',
                                                    overflow: 'hidden',
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        inset: 0,
                                                        borderRadius: '7px',
                                                        backgroundColor: '#f7dee0',
                                                        zIndex: 2,
                                                    }}
                                                />
                                                <Box
                                                    sx={{
                                                        position: 'relative',
                                                        zIndex: 7,
                                                        px: 0.6,
                                                        py: 0.2,
                                                        fontSize: 10.5,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        height: '100%',
                                                        color: '#0f172a',
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    <Box sx={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', pr: 0.5 }}>
                                                        {item.label}{item.note ? ` - ${item.note}` : ''}
                                                    </Box>
                                                    {item.singleDelete && (
                                                        <IconButton size="small" onClick={() => removeClosure(item.id)} sx={{ p: 0.2 }}>
                                                            <Delete sx={{ fontSize: 12 }} />
                                                        </IconButton>
                                                    )}
                                                </Box>
                                            </Box>
                                        );
                                    })}

                                    {appointments.map((item) => {
                                        const start = toMinutes(getTimeFromDate(item.startAt));
                                        const end = toMinutes(getTimeFromDate(item.endAt));
                                        if (start === null || end === null || end <= start) return null;

                                        const clampedStart = clamp(start, timeline.start, timeline.end);
                                        const clampedEnd = clamp(end, timeline.start, timeline.end);
                                        if (clampedEnd <= clampedStart) return null;

                                        const top = minuteToY(clampedStart, timeline);
                                        const height = Math.max(18, minuteToY(clampedEnd, timeline) - top);

                                        return (
                                            <Box
                                                key={`appointment-${item.id}`}
                                                onClick={() => navigate(getRoutePathname('crm_appointment', { id: item.id }))}
                                                sx={{
                                                    position: 'absolute',
                                                    top: `${top}px`,
                                                    left: `${LABEL_WIDTH + 4}px`,
                                                    right: '4px',
                                                    minHeight: '18px',
                                                    height: `${height}px`,
                                                    borderRadius: '7px',
                                                    cursor: 'pointer',
                                                    overflow: 'hidden',
                                                }}
                                                title={`${moment(item.startAt).format('HH:mm')} - ${moment(item.endAt).format('HH:mm')} • ${item.customerLastname ?? ''} ${item.customerFirstname ?? ''}${item.status ? ` (${item.status})` : ''}`}
                                            >
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        inset: 0,
                                                        borderRadius: '7px',
                                                        backgroundColor: '#dbe7fa',
                                                        zIndex: 2,
                                                    }}
                                                />
                                                <Box
                                                    sx={{
                                                        position: 'relative',
                                                        zIndex: 7,
                                                        px: 0.6,
                                                        py: 0.2,
                                                        fontSize: 10.5,
                                                        height: '100%',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        overflow: 'hidden',
                                                        whiteSpace: 'nowrap',
                                                        textOverflow: 'ellipsis',
                                                        color: '#0f172a',
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    {moment(item.startAt).format('HH:mm')} - {moment(item.endAt).format('HH:mm')} • {item.customerLastname ?? ''} {item.customerFirstname ?? ''}{item.status ? ` (${item.status})` : ''}
                                                </Box>
                                            </Box>
                                        );
                                    })}

                                    {timeline && isToday && (() => {
                                        const nowMinute = (nowTick.hours() * 60) + nowTick.minutes();
                                        if (nowMinute < timeline.start || nowMinute > timeline.end) {
                                            return null;
                                        }
                                        const top = minuteToY(nowMinute, timeline);
                                        return (
                                            <React.Fragment>
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        top: `${top}px`,
                                                        left: `${LABEL_WIDTH}px`,
                                                        right: 0,
                                                        borderTop: '2px solid #dc2626',
                                                        zIndex: 6,
                                                        pointerEvents: 'none',
                                                    }}
                                                />
                                                <Typography
                                                    sx={{
                                                        position: 'absolute',
                                                        top: `${top - 9}px`,
                                                        left: '6px',
                                                        fontSize: 10,
                                                        color: '#991b1b',
                                                        fontWeight: 800,
                                                        zIndex: 8,
                                                        pointerEvents: 'none',
                                                        backgroundColor: 'rgba(255, 255, 255, 0.92)',
                                                        px: 0.4,
                                                        borderRadius: '4px',
                                                    }}
                                                >
                                                    Maintenant
                                                </Typography>
                                            </React.Fragment>
                                        );
                                    })()}

                                    {timeline.timeMarks.map((minute, idx) => {
                                        const top = minuteToY(minute, timeline);
                                        const isLast = idx === timeline.timeMarks.length - 1;
                                        const isHourMark = minute % 60 === 0;
                                        return (
                                            <Box key={`${day.isoDate}-mark-${minute}`}>
                                                {isHourMark && (
                                                    <Typography
                                                        sx={{
                                                            position: 'absolute',
                                                            top: `${top - 8}px`,
                                                            left: '6px',
                                                            fontSize: 10,
                                                            color: '#4b5563',
                                                            fontWeight: 700,
                                                            zIndex: 7,
                                                            pointerEvents: 'none',
                                                            backgroundColor: 'rgba(248, 250, 252, 0.9)',
                                                            px: 0.2,
                                                            borderRadius: '4px',
                                                        }}
                                                    >
                                                        {String(Math.floor(minute / 60)).padStart(2, '0')}h
                                                    </Typography>
                                                )}
                                                {!isLast && (
                                                    <Box
                                                        sx={{
                                                            position: 'absolute',
                                                            top: `${top}px`,
                                                            left: 0,
                                                            right: 0,
                                                            borderTop: isHourMark ? '1px solid #c5ceda' : '1px dotted #d8dee8',
                                                            zIndex: 6,
                                                            pointerEvents: 'none',
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                        );
                                    })}
                                </Box>
                            )}
                        </Box>
                    );
                })}
            </Box>
        </ContentViewComponent>
    );
}

export default CrmAppointmentList;
